/**
 * SnoozeButton — a snooze trigger + quick-pick popover, shared by the Today
 * worklist, the Leads inbox, and the Active / No-reply conversation lists.
 *
 * Snoozing defers a lead off the working views until a chosen time, after which
 * it resurfaces in normal order (the backend filters crm.contacts.snoozed_until).
 * The parent owns the optimistic update + the PATCH via `onSnooze(untilIso|null)`
 * — this component is pure UI: the menu, the time math, and (when already
 * snoozed) an Un-snooze action.
 *
 * The menu is position:fixed (anchored to the trigger on open) so it never gets
 * clipped by an ancestor's overflow — notably the inbox's horizontally
 * scrollable table — and flips above the trigger when it would overflow the
 * viewport bottom (long inbox/Active lists).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Clock, Loader2, MoonStar, Undo2 } from 'lucide-react';
import { Z } from './ui';

/** True when an ISO timestamp is a still-active (future) snooze. */
export function isActiveSnooze(iso?: string | null): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t > Date.now();
}

/** "3:00 PM" today, else "Mon, Jun 15 3:00 PM". */
export function snoozeLabel(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} ${time}`;
}

/** Local-time value for a <input type="datetime-local">. */
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

interface SnoozeButtonProps {
  /** A future value → already snoozed (the menu offers Un-snooze + tints the icon). */
  snoozedUntil?: string | null;
  busy?: boolean;
  onSnooze: (untilIso: string | null) => void;
  /** Trigger button classes; defaults to the standard row-action look. */
  buttonClassName?: string;
  /** Trigger icon classes (default h-4 w-4). */
  iconClassName?: string;
  /** aria-label suffix, e.g. the contact name. */
  label?: string;
}

const DEFAULT_BTN =
  'inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8';

export default function SnoozeButton({
  snoozedUntil,
  busy,
  onSnooze,
  buttonClassName,
  iconClassName = 'h-4 w-4',
  label,
}: SnoozeButtonProps) {
  const [open, setOpen] = useState(false);
  const [pickValue, setPickValue] = useState('');
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const active = isActiveSnooze(snoozedUntil);

  const openMenu = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    setPickValue('');
    setOpen(true);
  };

  // After the menu renders, flip it above the trigger if it would overflow the
  // viewport bottom (so the Pick-a-time controls stay reachable on long lists).
  useLayoutEffect(() => {
    if (!open) return;
    const r = triggerRef.current?.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 0;
    if (!r || !menuH) return;
    const right = Math.max(8, window.innerWidth - r.right);
    let top = r.bottom + 4;
    if (top + menuH > window.innerHeight - 8) top = Math.max(8, r.top - menuH - 4);
    setPos((prev) => (prev && prev.top === top && prev.right === right ? prev : { top, right }));
  }, [open]);

  // Close on outside click, Escape, scroll, or resize (a moved trigger strands it).
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const fire = (iso: string | null) => {
    setOpen(false);
    setPickValue('');
    onSnooze(iso);
  };
  const doneForToday = () => {
    const d = new Date();
    d.setHours(24, 0, 0, 0); // local midnight tonight → resurfaces tomorrow
    fire(d.toISOString());
  };
  const laterToday = () => fire(new Date(Date.now() + 3 * 3600e3).toISOString());
  const pickFuture = !!pickValue && new Date(pickValue).getTime() > Date.now();
  const pick = () => {
    if (pickFuture) fire(new Date(pickValue).toISOString());
  };

  return (
    <span className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else openMenu();
        }}
        disabled={busy}
        aria-label={label ? `Snooze ${label}` : 'Snooze'}
        aria-haspopup="true"
        aria-expanded={open}
        title={active && snoozedUntil ? `Snoozed until ${snoozeLabel(snoozedUntil)}` : 'Snooze'}
        className={buttonClassName || DEFAULT_BTN}
      >
        {busy ? (
          <Loader2 className={`${iconClassName} animate-spin`} />
        ) : (
          <MoonStar className={`${iconClassName}${active ? ' text-primary-600 dark:text-primary-400' : ''}`} />
        )}
      </button>

      {open && pos && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          className={`${Z.modal} w-48 rounded-lg border border-gray-200 bg-white p-1 text-left shadow-lg dark:border-gray-600 dark:bg-gray-800`}
        >
          {active && snoozedUntil && (
            <>
              <div className="px-2 pb-1 pt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Snoozed until {snoozeLabel(snoozedUntil)}
              </div>
              <button
                onClick={() => fire(null)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Undo2 className="h-3.5 w-3.5 text-gray-400" /> Un-snooze
              </button>
              <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
            </>
          )}
          <button
            onClick={doneForToday}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-primary-50 dark:text-gray-200 dark:hover:bg-primary-900/20"
          >
            <MoonStar className="h-3.5 w-3.5 text-primary-600" /> Done for today
          </button>
          <button
            onClick={laterToday}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <Clock className="h-3.5 w-3.5 text-gray-400" /> Later today (+3h)
          </button>
          <div className="mt-1 border-t border-gray-100 px-2 pb-1 pt-2 dark:border-gray-700">
            <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400">Pick a time</label>
            <input
              type="datetime-local"
              value={pickValue}
              min={toLocalInput(new Date())}
              onChange={(e) => setPickValue(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-1.5 py-1 text-xs dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
            <button
              onClick={pick}
              disabled={!pickFuture}
              className="mt-1.5 w-full rounded-md bg-primary-600 px-2 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              Snooze until then
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
